<template lang="pug">
.space-table(ref='table' :class='state')
	.search
		slot(name='search')
	.table-container
		tr.headers.fixed
			td(
				ref='fixedHeaders'
				v-for='col in cols'
				v-bind:class='[{\
					sorted : sortedColumn == col.key,\
					up     : sortedColumn == col.key && sortDirection == 1,\
					down   : sortedColumn == col.key && sortDirection == -1\
				}, col.columnClass, col.headerClass]'
			)
				.header
					p(@click='emitSort( col )' :title='col.title') {{ col.title }}
					slot(:name='`${col.key}Filter`')
		.table-wrapper(ref='wrapper')
			table(:class='state' v-if='cols.length')
				tr.headers
					td(
						ref='headers'
						v-for='col in cols'
					)
						.header
							p(:title='col.title') {{ col.title }}
							slot(:name='`${col.key}Filter`')

				tr.row.clickable(
					v-for='( row, rIndex ) in rows'
					v-if='state !== "loading"'
					:draggable='draggable'
					@dragstart='setDragData( $event, row )'
				)

					td.cell(
						v-for='( col, cIndex ) in cols'

						@click='emitCellClick( rIndex, col.key )'
						:class='col.columnClass'
					)

						//- regular column item
						p(v-html='( col.transformText ? col.transformText( row[col.key] ) : row[col.key] ) || ( col.default ) ')


			.loading-container(ref='loading')
				.loading-indicator.spacey Loading
					span .
					span .
					span .

			.failed-container(
				v-if='state === "failed"'
				@click='$emit( "fetch", offset, this.increment )'
			)
				p.error.spacey Failed To Load

			.no-cols-container(v-if='!Object.keys( cols ).length')
				p.spacey All columns hidden

			.no-results(v-if='( !rows.length && state !== "loading" ) && ( !rows.length && state !== "failed" )' ref='noResults')
				p No Results
	//* pagination on table
	.pagination(v-if='length > increment')
		.pagination-wrapper
			.arrows

				.icon-beginning(@click='setPage( 1 )')
				.icon-prev(@click='prevPage')

				p(
					v-for='i in pages'
					:class='page === i ? "active" : ""'
					@click='setPage( i )'
				) {{ i }}

				.icon-next(@click='nextPage')
				.icon-end(@click='setPage( totalPages )')

			.indicator
				p {{ paginationProgress }}

</template>

<script>
import { ObjToArray, clone } from '@/lib/utils';

export default {
	name : 'paginated-table-view',

	props : {
		'columns' : {
			required : true,
			type     : Object
		},
		'rows' : {
			required : true,
			default  : null
		},
		'draggable' : {
			type    : Boolean,
			default : false
		},
		'options' : {
			type    : Object,
			default : () => ( {} ),
		},
		'dataState' : {
			required : true,
			type     : String,
		},
		'length' : {
			required : true,
			type     : Number,
			default  : 0
		},
		'isFiltered' : {
			type : Boolean
		},
		'increment' : {
			type    : Number,
			default : 10,
		}
	},

	data : () => ( {
		page : 1,

		paginationNumber : 5, // should be odd

		sortedColumn  : null,
		sortDirection : -1,

		state : 'init',
	} ),

	computed : {
		cols() {
			/* Order all of the columns */
			const columns          = ObjToArray( clone( this.columns ) ).filter( a => !a.hidden );
			const orderedColumns   = [];
			const positionTaken    = {};
			const unorderedColumns = [];

			// first, sort the columns into those with an
			// order and those without
			columns.forEach( ( column ) => {
				if ( column.order !== undefined ) {
					// check if there is a conflict in orders
					// e.g. two columns have the same order
					if ( positionTaken[column.order] ) {
						throw new Error( `Column with order ${column.order} already taken` );
					}
					// mark this position as taken
					// and add it into the ordered columns
					positionTaken[column.order] = true;
					orderedColumns.push( column );
					return; // eslint-disable-line
				}
				// this column has no order, and we'll assign
				// it an order based on the orders of the other
				// columns
				unorderedColumns.push( column );
			} );
			let highestFilledOrder = 0;
			const autoOrderedColumns = unorderedColumns.map( ( a, i ) => { // eslint-disable-line
				// start off looking for an opening at
				// the order of the last order where we
				// know that all orders previous to it
				// are taken
				let order = highestFilledOrder;
				while ( positionTaken[order] ) {
					order++; // eslint-disable-line
				}
				// indicate the now highest filled order
				// and set that to the position of this
				// column
				highestFilledOrder = order;
				a.order            = order; // eslint-disable-line
				positionTaken[order] = true;
				return a;
			} );
			// combine the explicitly ordered columns with
			// the columns which we ordered manually and then
			// sort them
			const sortedColumns = orderedColumns.concat( autoOrderedColumns ).sort( ( a, b ) => { // eslint-disable-line
				return a.order - b.order;
			} );
			// finally, we'll fill in all of the default properties
			// of the columns
			return sortedColumns.map( ( a ) => {
				const defaultColumn = {
					default     : '-',
					name        : '',
					class       : '',
					columnClass : '',
					style       : {},
					ascending   : true,
				};
				const defaultedColumn = Object.assign( defaultColumn, a );
				if ( defaultedColumn.width === 'min' ) {
					defaultedColumn.class += ' min-width';
				}
				const w = defaultedColumn.width;
				defaultedColumn.style.flex = `${w} ${w} 0`;
				if ( defaultedColumn.type === 'control' || defaultColumn.type === 'icon' ) {
					defaultedColumn.style.flex = '0 0 auto';
				}
				return defaultedColumn;
			} );
		},

		settings() {
			const defaultOptions = {
				loadingText       : 'loading',
				searchPlaceholder : 'Search',
			};
			return Object.assign( defaultOptions, this.options );
		},

		totalPages() {
			const { length, increment } = this;

			return Math.ceil( length / increment );
		},

		offset() {
			const { page, increment } = this;

			return ( page - 1 ) * increment;
		},

		startPage() {

			const { page, totalPages, paginationNumber } = this;

			if ( page <= 3 ) {
				return 1;
			}

			const pageIncrement = Math.floor( paginationNumber / 2 );

			if ( page + pageIncrement > totalPages ) {
				return totalPages - paginationNumber + 1;
			}

			return page - pageIncrement;

		},

		pages() {

			const range = [];
			const { startPage, paginationNumber, totalPages } = this;

			if ( paginationNumber > totalPages ) {
				for ( let i = 0; i < totalPages; i++ ) {
					range.push( startPage + i );
				}

				return range;
			}

			for ( let i = 0; i < paginationNumber; i++ ) {
				range.push( startPage + i );
			}

			return range;

		},

		paginationProgress() {
			const { offset, increment, length } = this;

			return `${offset + 1}-${offset + increment} of ${length}`;
		}

	},

	watch : {

		increment : {
			immediate : true,

			handler( increment ) {
				this.$emit( 'increment', increment );
			},
		},

		dataState : {
			immediate : true,
			handler( state ) {

				this.state = state;

				if ( state === 'search' ) {
					this.setPage( 1 );
					return;
				}

				if ( state === 'loading' ) {

					this.$nextTick( () => {
						this.$refs.loading.style.left = `${this.$refs.wrapper.scrollLeft}px`;
					} );

				}

				this.$nextTick( () => {

					if ( this.state === 'idle' && !this.rows.length ) {
						this.$refs.noResults.style.left = `${this.$refs.wrapper.scrollLeft}px`;
					}

				} );


			}
		},

		offset : {
			immediate : true,

			handler( offset ) {
				this.$emit( 'offset', offset );

				if ( this.state === 'idle' || this.state === 'search' ) {
					this.$emit( 'fetch', offset, this.increment );
				}
			},

		},

		rows( rows ) {

			if ( !rows || !rows.length ) {
				return;
			}

			this.$nextTick( () => {
				this.resizeHeaders();
			} );
		}
	},

	methods : {

		setDragData( e, row ) {
			e.dataTransfer.setData( 'data', JSON.stringify( row ) );
		},

		setPage( newPage ) {
			this.page = newPage;
		},

		nextPage() {
			const { page, totalPages } = this;

			if ( page < totalPages ) {
				this.page++;
			}

		},

		prevPage() {
			const { page } = this;

			if ( page > 1 ) {
				this.page--;
			}

		},

		resizeHeaders() {
			const { fixedHeaders, headers } = this.$refs;

			headers.forEach( ( header, i ) => {
				const { width } = header.getBoundingClientRect();
				const fixedHeader = fixedHeaders[i];

				fixedHeader.style.minWidth = `${width}px`;
			} );
		},

		/* * * * * * * * * *
		 * emitted events  *
		 * * * * * * * * * */
		emitSort( column ) {
			const { sortDirection } = this;
			const { sort, key } = column;

			this.sortDirection = sortDirection === 1 ? -1 : 1;

			this.$emit( 'sort', sort, key, sortDirection );
		},

		emitCellClick( rIndex, cKey ) {
			// determine data to emit
			const row  = this.rows[rIndex];
			const col  = this.columns[cKey]; // eslint-disable-line
			const cell = {
				key   : cKey,
				value : row[cKey]
			};
			// still emit a cell-clicked idk if anybody even uses
			// this still
			this.$emit( 'row-clicked', rIndex, row );
			this.$emit( 'cell-clicked', cell );
		},

		emitClearFilter() {
			this.$emit( 'clear-filter' );
		},

	},

	mounted() {
		window.addEventListener( 'resize', () => {
			this.resizeHeaders();
		} );
	}
};
</script>

<style scoped lang="scss">

@media only screen and ( max-width: 640px ) {

	.indicator p::before {
		content : ' ' !important;
	}
}

.space-table {
	position: relative;

	&.loading {

		.loading-container {
			display: block !important;
		}
	}

	.table-container {
		max-height: 100%;
    margin-bottom: 48px;
		position: relative;
		overflow-x: auto;
		overflow-y: hidden;
		z-index: 9;

		// This was moved from being a child of table
		// This is so that the fixed header and table header use the same styling
		tr {
			display: table-row;

			td {
				display: table-cell;
				padding: 15px 5px;

				&:nth-child(1) {
					padding-left: 30px;
				}

				&:nth-last-child(1) {
					padding-right: 15px!important;
				}

				p {
					font-size: 14px;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
				}
			}

			&.row {
				border-bottom: 1px solid rgba(80, 96, 110, 0.17);

				&:hover,
				&.active {
					background: $lightGrey;
				}

				&.active td p {
					color: $color-primary;
					font-weight: 500;
				}
			}
		}

		.headers {
			pointer-events: none;
			opacity: 0;

			.header {
				position: relative;
				display: flex;
				align-items: center;
			}

			td {

				p {
					cursor: pointer;
					font-size: 14px !important;
					// ! This is actually the primary color,
					// ! but in variables, the primary color is green,
					// ! when in reality, it should be grey and the
					// ! accent color or secondary color would be green.
					color: white;
					font-weight: 500;
					margin-right: 10px;
				}
			}
		}

		.fixed {
			position: absolute;
			z-index: 11;
			background: white;
			opacity: 1;
			pointer-events: all;

			p {
				color: $color-secondary !important;
			}
		}
	}

	.table-wrapper {
		min-width: 100%;
		display: inline-block;
		height: 100%;
		position: relative;
		overflow-x: hidden;
		overflow-y: auto;

		table {
			border-collapse: collapse;
			min-width: 100%;
			position: relative;
		}

		.loading-container {
			display: none;
			width: 100%;
			position: absolute;
			left: 0;

			.loading-indicator {
				font-size: 16px;
				width: 100%;
				text-align: center;
			}
		}

		.no-results, .failed-container {
			background: white;
			width: 100%;
			position: absolute;
			display: block;
			pointer-events: none;

			p {
				width: 100%;
				text-align: center;
				font-size: 16px;
			}
		}

		.failed-container {
			cursor: pointer;
			pointer-events: all;
		}

		.no-cols-container {
			margin-top: 60px;

			p.spacey {
				text-align: center;
			}
		}
	}

	.pagination {
		padding: 20px 0 0 0;
		z-index: 2;
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		overflow: hidden;
		pointer-events: none;

		* {
			pointer-events: all;
		}

		.pagination-wrapper {
			background: white;
			box-shadow: $box-shadow__card;
			padding: 7px 15px;
			display: flex;
			justify-content: space-between;
		}

		.arrows {
			display: flex;
			flex-flow: row;
			align-items: center;

			> *{
				margin: 5px;
				font-size: 14px;
				&:hover {
					cursor: pointer;
				}
			}

			[class^="icon-"], [class*=" icon-"] {
				&::before {
					color: #A8B4BE;
				}
			}

			p {
				text-align: center;
				height: 25px;
				display: flex;
				justify-content: center;
				align-items: center;

				&.active {
					width: 25px;
					background-color: darken( $color-primary, 15% );
					color: white!important;
					border-radius: 3px;
					text-align: center;
				}
			}
		}

		.indicator {
			display: flex;
			align-items: center;

			p::before {
				content: 'Showing ';
			}
		}
	}
}
</style>
